/**
 * Script Composer → Toolbox chat hand-off (behind Flags.canUseToolbox()).
 *
 * Instead of the classic scene-generation pipeline, finishing the composer
 * stages the script and the user's choices as a chat message for the in-creator
 * agent to build from. Kept in its own module while feature-flagged; expected
 * to become the main flow.
 */
import { TemplateColorProperties } from '@lumen5/luminary';

import { ApiService } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    saveDashboardSketchData,
    type SerializedDocument,
    type SerializedMedia,
} from 'js/src/components/dashboard/utils/dashboardSketchStorage';
import { SupportedFileType } from 'js/src/components/sketch/FileUpload';
import { getAIVoiceById } from 'js/src/data/ai-voices-and-avatars';
import {
    EDITOR_TAB_OPTIONS,
    getSelectedAspectRatio,
    getSelectedBlueprintId,
    getSelectedPaletteIndex,
    getSelectedTemplateId,
    getVideoIdOrThrow,
    getVideoInitType,
    setScriptComposerPlaceholderVideoId,
    setStage,
    setToolboxCreatingVideo,
    setVideoId,
    setVideoSlug,
    STAGES,
    switchEditorTabAction,
    VIDEO_INIT_TYPE,
} from 'js/src/data/app';
import { selectArticleId } from 'js/src/data/article/article-selectors';
import { getBlueprint } from 'js/src/data/blueprints';
import { getBrandKitById } from 'js/src/data/brand-kits';
import { selectDocumentName, selectDocumentUrl } from 'js/src/data/document';
import { setDocToScriptPage } from 'js/src/data/script-composer/script-composer-actions';
import {
    getCurrentAIAvatar,
    getDocToScriptImportType,
    getEditedScriptLanguage,
    getEnforceBlockSegmentation,
    getReferenceMaterial,
} from 'js/src/data/script-composer/script-composer-selectors';
import { getTemplateColorPalettes } from 'js/src/data/templates';
import { getBrandKitId } from 'js/src/data/video';
import { requestChatHandoffConsume } from 'js/src/libs/chat-handoff-event-emitter';
import { SCRIPT_CREATION_SOURCES } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import uuid from 'js/src/libs/uuid';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';

export interface ToolboxHandoffMessageOptions {
    scriptLines: string[];
    videoInitType: VIDEO_INIT_TYPE;
    language?: string | null;
    voiceName?: string | null;
    avatarName?: string | null;
    templateId?: string | null;
    aspectRatio?: string | null;
    brandKit?: { id: number; name: string } | null;
    palette?: { index: number; name: string; colors: string[] } | null;
    blueprint?: { id: number; name: string } | null;
    /** True for "create one scene per script block"; false lets the AI split. */
    enforceBlockSegmentation?: boolean;
    referenceMaterial?: string | null;
    /** True when the source document is attached to the chat message instead. */
    hasAttachedSourceDocument?: boolean;
}

/**
 * Builds the message handed to the Toolbox chat agent. The agent has no
 * dedicated "use-this-script" tool, so the script and the user's choices are
 * encoded as natural-language instructions.
 *
 * The script sits at the top as plain text and everything else goes inside
 * <video-request> tags: the chat renderer strips XML-tagged sections from user
 * messages, so the user's message bubble shows exactly the script they wrote.
 */
export const buildToolboxHandoffMessage = ({
    scriptLines,
    videoInitType,
    language,
    voiceName,
    avatarName,
    templateId,
    aspectRatio,
    brandKit,
    palette,
    blueprint,
    enforceBlockSegmentation,
    referenceMaterial,
    hasAttachedSourceDocument,
}: ToolboxHandoffMessageOptions): string => {
    const script = scriptLines
        .map((line) => line.trim())
        .filter(Boolean)
        .join('\n\n');

    const planDetails: string[] = [];
    if (videoInitType === VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR) {
        planDetails.push('- Narration: AI avatar presenter');
    } else if (videoInitType === VIDEO_INIT_TYPE.TEXT_TO_AI_VOICEOVER) {
        planDetails.push('- Narration: AI voiceover');
    } else {
        planDetails.push('- Narration: text on media (no voiceover)');
    }
    if (avatarName) {
        planDetails.push(`- Avatar presenter: ${avatarName}`);
    }
    if (voiceName) {
        planDetails.push(`- Voice: ${voiceName}`);
    }
    if (language) {
        planDetails.push(`- Language: ${language}`);
    }
    if (aspectRatio) {
        planDetails.push(`- Aspect ratio: ${aspectRatio}`);
    }
    if (brandKit) {
        planDetails.push(
            `- Brand kit: "${brandKit.name}" (id ${brandKit.id}) — apply it to the video with ` +
                'the apply_brand_kit tool.',
        );
    } else if (palette?.colors.length) {
        planDetails.push(
            `- Color palette chosen by the user: "${palette.name}" (palette index ` +
                `${palette.index} of the template), colors: ${palette.colors.join(', ')} — use ` +
                "these as the video's colors.",
        );
    }
    if (blueprint) {
        planDetails.push(
            `- The user based this video on the "${blueprint.name}" blueprint (id ${blueprint.id}).`,
        );
    }
    if (enforceBlockSegmentation) {
        planDetails.push(
            '- Scene structure: create exactly one scene per script paragraph (the ' +
                'blank-line-separated blocks above), in order — do not merge or re-split them.',
        );
    }
    if (hasAttachedSourceDocument) {
        planDetails.push(
            '- The attached document is the source material the script was derived from. Use it ' +
                'for context (e.g. finding relevant media) — it is NOT the script.',
        );
    }

    const referenceText = referenceMaterial?.trim();

    const lines: string[] = [script, '', '<video-request>'];

    if (templateId) {
        lines.push(
            `Create the video by calling create_blank_video with template_id "${templateId}"` +
                (aspectRatio ? ` and aspect_ratio "${aspectRatio}"` : '') +
                ' (use these exact values if available; otherwise pick the closest match).',
        );
    }

    lines.push(
        'The text above this request block is the video script. Build the video using that ' +
            'exact script, keeping the wording verbatim.',
        '',
        'Plan:',
        ...planDetails,
    );

    if (referenceText) {
        lines.push(
            '',
            'The following is the source/reference material the script was derived from. ' +
                'Use it for context (e.g. finding relevant media) — it is NOT the script:',
            '<reference-material>',
            referenceText,
            '</reference-material>',
        );
    }

    lines.push('</video-request>');

    return lines.join('\n').trim();
};

const DOCUMENT_EXTENSION_TO_FILE_TYPE: Record<string, SupportedFileType> = {
    pdf: SupportedFileType.PDF,
    docx: SupportedFileType.DOCX,
    pptx: SupportedFileType.PPTX,
    txt: SupportedFileType.TXT,
    csv: SupportedFileType.CSV,
    tsv: SupportedFileType.TSV,
    xml: SupportedFileType.XML,
};

// The composer only stores the source document's name/url, not its mime type.
function inferDocumentFileType(fileName: string): SupportedFileType {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
    return DOCUMENT_EXTENSION_TO_FILE_TYPE[extension] ?? SupportedFileType.PDF;
}

/**
 * Collects the images the user attached in the composer as serialized media for
 * the hand-off; the chat re-hydrates them into the MediaRegistry by uuid.
 * Returns [] on failure — partial success beats blocking the hand-off.
 */
async function collectComposerImages(articleId: number | null): Promise<SerializedMedia[]> {
    if (articleId == null) {
        return [];
    }
    try {
        const response = await ApiService.articleImagesList({
            path: { article_pk: articleId },
        });
        const images = response.data ?? [];
        return images
            .filter((image): image is typeof image & { uuid: string } => !!image.uuid)
            .map((image, index) => ({
                id: image.uuid,
                name: `Attached image ${index + 1}`,
                type: SupportedFileType.PNG,
                uuid: image.uuid,
            }));
    } catch (error) {
        if (error instanceof Error) {
            ErrorReporter.capture(error);
        }
        return [];
    }
}

/**
 * Parks the current DB video as a placeholder and drops the user into the
 * toolbox chat's video-less build experience: wipes the video's scene groups,
 * unloads the video id (the parked DB video is adopted at turn end instead of
 * creating a second one — see createSingleVideoAtTurnEndOperation), switches to
 * the chat tab, and signals the chat to consume the staged hand-off message.
 * The message must already be staged (saveDashboardSketchData) before calling.
 */
export const parkVideoAndEnterChatOperation = createLuminaryOperation(
    async ({ luminary, dispatch, getState }) => {
        const videoId = getVideoIdOrThrow(getState());

        // Wipe the placeholder's default scenes so they don't survive into the
        // agent's build. Raw luminary API on purpose: the app-level delete
        // operation refuses to remove the last ordinary scene.
        [...luminary.sceneGroups].forEach((sceneGroup) => {
            luminary.removeSceneGroup(sceneGroup.id);
        });

        dispatch(setScriptComposerPlaceholderVideoId(videoId));
        // Mark the build in progress in the same batch that unloads the video
        // id, so the generating preview replaces the scene list in the very
        // render that drops the id — otherwise the scene list re-runs its
        // selectors against a null video id and crashes on getVideoIdOrThrow.
        dispatch(setToolboxCreatingVideo(true));
        dispatch(setVideoId({ videoId: null }));
        dispatch(setVideoSlug({ videoSlug: null }));
        dispatch(switchEditorTabAction({ nextTab: EDITOR_TAB_OPTIONS.CHAT }));
        dispatch(setDocToScriptPage({ page: null }));
        dispatch(setStage({ stage: STAGES.CREATOR }));

        // The chat consumes staged messages on mount, but here it is already
        // mounted — signal it to consume this one; consuming is idempotent.
        requestChatHandoffConsume();
    },
);

interface ToolboxHandoffArgs {
    scriptLines: string[];
    currentAIVoiceId: string | null;
    scriptWordCount: number;
}

/**
 * Finishes the script composer by handing off to the Toolbox chat: stages the
 * message (with the source doc/images as attachments), closes the composer, and
 * switches to the in-creator chat.
 *
 * The video created when the composer opened is parked as a placeholder and
 * unloaded, so the creator behaves exactly like the video-less toolbox build
 * (create_blank_video proceeds, controls stay hidden); the parked DB video is
 * adopted at turn end instead of creating a second one — see
 * createSingleVideoAtTurnEndOperation.
 */
export const handOffScriptComposerToChatOperation = createLuminaryOperation(
    async (
        { getState, call },
        { scriptLines, currentAIVoiceId, scriptWordCount }: ToolboxHandoffArgs,
    ) => {
        const state = getState();
        const videoId = getVideoIdOrThrow(state);
        const videoInitType = getVideoInitType(state);
        const referenceMaterial = getReferenceMaterial(state);
        const documentName = selectDocumentName(state);
        const selectedTemplateId = getSelectedTemplateId(state);
        const selectedPaletteIndex = getSelectedPaletteIndex(state);
        const selectedPalette =
            selectedTemplateId && selectedPaletteIndex !== null
                ? (getTemplateColorPalettes(state, selectedTemplateId).get(selectedPaletteIndex) ??
                  null)
                : null;
        const brandKitId = getBrandKitId(state);
        const brandKit = brandKitId !== null ? getBrandKitById(state, brandKitId) : null;
        const selectedBlueprint = getBlueprint(state, getSelectedBlueprintId(state));
        const currentAIVoice = currentAIVoiceId ? getAIVoiceById(state, currentAIVoiceId) : null;
        const currentAIAvatar =
            videoInitType === VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR ? getCurrentAIAvatar(state) : null;

        // An uploaded source doc travels as a chat attachment (its extracted
        // text included), so the message skips the reference-material block.
        const sourceDocument: SerializedDocument | null =
            getDocToScriptImportType(state) === SCRIPT_CREATION_SOURCES.DOCUMENT && documentName
                ? {
                      id: uuid(),
                      name: documentName,
                      type: inferDocumentFileType(documentName),
                      url: selectDocumentUrl(state) ?? '',
                      text: referenceMaterial,
                  }
                : null;

        const messageText = buildToolboxHandoffMessage({
            scriptLines,
            videoInitType,
            language: getEditedScriptLanguage(state),
            voiceName: currentAIVoice?.display_name ?? null,
            avatarName: currentAIAvatar?.get('name') ?? null,
            templateId: selectedTemplateId,
            aspectRatio: getSelectedAspectRatio(state),
            brandKit:
                brandKitId !== null && brandKit
                    ? { id: brandKitId, name: brandKit.get('name') }
                    : null,
            palette:
                selectedPalette && selectedPaletteIndex !== null
                    ? {
                          index: selectedPaletteIndex,
                          name: selectedPalette.get('name'),
                          colors: selectedPalette
                              .get('colors')
                              .toArray()
                              .map((color) => String(color.get(TemplateColorProperties.COLOR))),
                      }
                    : null,
            blueprint: selectedBlueprint
                ? { id: selectedBlueprint.id, name: selectedBlueprint.name }
                : null,
            enforceBlockSegmentation: getEnforceBlockSegmentation(state),
            referenceMaterial: sourceDocument ? null : referenceMaterial,
            hasAttachedSourceDocument: !!sourceDocument,
        });

        const media = await collectComposerImages(selectArticleId(state));

        const staged = saveDashboardSketchData({
            messageText,
            documents: sourceDocument ? [sourceDocument] : [],
            media,
        });
        if (!staged) {
            Toast.error('Unable to start building your video. Please try again.');
            return;
        }

        analytics.track('Script Composer handed off to Toolbox chat', {
            videoId,
            videoType: videoInitType,
            voice: currentAIVoiceId,
            script_word_count: scriptWordCount,
            image_count: media.length,
            has_source_document: !!sourceDocument,
        });

        await call(parkVideoAndEnterChatOperation);
    },
);
